import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgClass, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { filter, switchMap } from 'rxjs';

import { CmsService, ScriptLoader } from '@spartacus/core';

import {
  ReviewEnum, ThemesEnum, SdNavigationFooterComponent, INavigationFooter, IServiceOptions,
  IContact, ILocation, IReviewSkeeper, BrandsEnum
} from '@seb/web-design-system/core';

import { HobStateService } from '@seb/web-common/core/services';

import {
  CustomCmsLinkComponent,
  NavigationBlocModuleInterface,
  NavigationFooterModuleInterface, RatingAndReviewsWidget
} from '../../../models';

import { AbstractModuleComponent } from '../abstract-module.component';
import { mapPageTemplateToPageType, waitForElm, waitForElmWithChildren } from '../../../utils';
import { BaseStoreService, ImageService } from '../../../services';
import { ScriptPositionEnum, WidgetTypes } from '../../../enums';
import { LinkMapper } from '../../../mappers';

@Component({
  selector: 'wc-navigation-footer-module',
  templateUrl: './navigation-footer-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    SdNavigationFooterComponent
  ]
})
export class NavigationFooterModuleComponent extends AbstractModuleComponent<NavigationFooterModuleInterface> implements OnInit {
  navigationFooters: INavigationFooter[];
  serviceOptions: IServiceOptions[];
  contact: IContact;
  location: ILocation;
  themesConfig: ThemesEnum.light | ThemesEnum.dark;
  contentReview: IReviewSkeeper;
  contentReviewWidget: RatingAndReviewsWidget = null;
  currentBrandSkp;
  isHob: boolean;
  activeBrand: BrandsEnum;

  constructor(
    private cmsService: CmsService,
    private cdRef: ChangeDetectorRef,
    protected baseStoreService: BaseStoreService,
    private router: Router,
    private imageService: ImageService,
    private readonly scriptLoader: ScriptLoader,
    private readonly hobStateService: HobStateService
  ) {
    super();
  }

  override mandatoryComponentFunction = (component: NavigationBlocModuleInterface): boolean => {
    return !!component?.headline;
  };

  ngOnInit(): void {
    this.initPageThemeAndReviews();

    this.subscriptions.add(
      this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand }) => {
        this.activeBrand = liveBrand || siteBrand;
        this.isHob = isHob;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.module$.pipe(
        switchMap((module) => this.componentService.getLazyComponentData(
          module,
          'serviceOptionsComponents',
          this.mandatoryComponentFunction
        )),
        switchMap((module) => this.componentService.getSubComponentsLazyComponentData(
          module,
          'componentsObjects',
          'components',
          this.verifyMandatoryLinks
        )),
        switchMap((module) => this.componentService.getSubComponentsLazyComponentData(
          module,
          'serviceOptionsComponentsObjects',
          'serviceOptionsComponents',
          null
        )),
      ).subscribe((module: NavigationFooterModuleInterface) => {
        this.contact =
          module.contactTitle && module.contactEmail && module.contactPhone
            ? this.toContact(module)
            : undefined;

        this.location =
          module.letterContactTitle && module.letterContactAddress
            ? this.toLocation(module)
            : undefined;

        this.navigationFooters = this.toNavigationFooters(module);
        this.serviceOptions = this.toServiceOptionsObjects(module);

        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  verifyMandatoryLinks = (link: CustomCmsLinkComponent): boolean => (
    ((!!link.url || !!link.urlLocalized) && !!link.linkName) || !!link.medias
  );

  private initPageThemeAndReviews(): void {
    this.subscriptions.add(
      this.cmsService.getCurrentPage().pipe(filter((page) => !!page)).subscribe((page) => {
        const pageType = mapPageTemplateToPageType(page, this.router.parseUrl(this.router.url));
        const baseStore = this.baseStoreService.baseStore;

        if (baseStore.activateRatingAndReviews) {
          const reviews = baseStore.ratingAndReviewsWidgets?.find((reviewWidget) => {
            if (reviewWidget.widgetType === WidgetTypes.SCRIPT) {
              return reviewWidget.pagesTypes?.includes(pageType) && reviewWidget.position?.includes(ScriptPositionEnum.FOOTER);
            } else {
              return [];
            }
          });
          this.contentReviewWidget = reviews;
          this.contentReview = this.contentReviewWidget?.content ? this.getReviewSkeeper(this.contentReviewWidget?.content) : undefined;
          this.currentBrandSkp = baseStore?.brandSite?.toLowerCase();
          this.initWidgetSkeepers();
        }

        this.themesConfig = page['headerTheme'] ? ThemesEnum[page['headerTheme']?.toLowerCase()] : ThemesEnum.light;
      })
    );
  }

  private getReviewSkeeper(skeeperUrl: string) {
    if (skeeperUrl.includes('carousel')) {
      return {
        id: 'skeepers_carousel_container',
        type: ReviewEnum.CAROUSEL
      };
    }

    return {
      id: skeeperUrl.split('/').pop().split('.')[0],
      type: ReviewEnum.IMAGE
    };
  }

  private initWidgetSkeepers(): void {
    const scriptAttributes = this.contentReviewWidget?.contentIntegrity
      ? {
        integrity: this.contentReviewWidget?.contentIntegrity,
        crossOrigin: 'anonymous',
        id: 'skeepers-script'
      }
      : { id: 'skeepers-script' };

    this.loadScriptWithTimeout(scriptAttributes);
  }

  private loadScriptWithTimeout(scriptAttributes: { integrity?: string; crossOrigin?: string; id: string }): void {
    const scriptUrl = this.contentReviewWidget?.content;
    const scriptId = this.contentReview?.id;

    this.scriptLoader.embedScript({
      src: scriptUrl,
      attributes: scriptAttributes,
      callback: () => {
        if (isPlatformBrowser(this.platformId)) {
          if (scriptId === 'skeepers_carousel_container') {
            waitForElm('.' + scriptId).then(() => {
              if (window['SkeepersCarouselWidget']) {
                window['SkeepersCarouselWidget'].generate();
                waitForElmWithChildren('.' + scriptId + ' .carousel-container').then(() => {
                  setTimeout(() => {
                    window['SkeepersCarouselWidget'].generate();
                  }, 2000);
                });
              }
            });
          } else {
            waitForElm('#' + scriptId).then(() => {
              if (window['SkeepersBrandWidget']) {
                window['SkeepersBrandWidget'].generate();
              }
            });
          }
        }
      },
      errorCallback: () => {
        new Error(`Error loading ${scriptUrl}`);
      }
    });
  }

  private toContact(module: NavigationFooterModuleInterface): IContact {
    return {
      title: module.contactTitle,
      mail: module.contactEmail,
      phone: module.contactPhone
    };
  }

  private toLocation(module: NavigationFooterModuleInterface): ILocation {
    return {
      title: module.letterContactTitle,
      address: module.letterContactAddress
    };
  }

  private toNavigationFooters(module: NavigationFooterModuleInterface): INavigationFooter[] {
    return module.componentsObjects.map((block) => ({
      title: block.headline,
      children: block.componentsObjects.map((cmp) => {
        if (cmp.medias) {
          const medias = Object.entries(cmp.medias).map((el) => el[1]);
          return { medias: medias.map((media) => this.imageService.normalizeMedia(media)) };
        } else {
          return LinkMapper.cmsLinkToILink(cmp);
        }
      })
    }));
  }

  private toServiceOptionsObjects(module: NavigationFooterModuleInterface): IServiceOptions[] {
    return module.serviceOptionsComponentsObjects
      .filter((cmp) => !!cmp.headline && !!cmp.medias)
      .map((cmp) => ({
        title: cmp.headline,
        medias: Object.keys(cmp.medias)
          .map((key)=>cmp.medias[key])
          .map((media)=> this.imageService.normalizeMedia(media))
      }));
  }
}
